// @ts-nocheck
// Magic Link login modal — Hebrew, RTL, matches the app's warm tone.
// Calls supabase.auth.signInWithOtp; the redirect URL needs to be on the
// Supabase allow-list (Authentication → URL Configuration).

import { useState } from "react";
import { supabase } from "../lib/supabase";
import { trackLead } from "../lib/fbTrack";

const styles = `
.auth-overlay { position: fixed; inset: 0; background: rgba(45,27,16,0.55); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; backdrop-filter: blur(4px); }
.auth-modal { background: #FFFDF9; border-radius: 20px; padding: 32px 28px; max-width: 420px; width: 100%; box-shadow: 0 20px 60px rgba(92,61,46,0.35); direction: rtl; font-family: 'Heebo', sans-serif; position: relative; }
.auth-close { position: absolute; top: 14px; left: 14px; background: none; border: none; cursor: pointer; font-size: 24px; color: #8B7355; line-height: 1; padding: 4px 8px; border-radius: 8px; }
.auth-close:hover { background: #F3EDE3; }
.auth-title { font-family: 'Frank Ruhl Libre', serif; font-size: 24px; font-weight: 700; color: #5C3D2E; margin-bottom: 8px; }
.auth-subtitle { font-size: 14px; color: #8B7355; line-height: 1.6; margin-bottom: 24px; }
.auth-form { display: flex; flex-direction: column; gap: 14px; }
.auth-label { font-size: 13px; font-weight: 600; color: #5C3D2E; }
.auth-input { width: 100%; padding: 12px 14px; border: 2px solid #E8DCC8; border-radius: 12px; font-family: 'Heebo', sans-serif; font-size: 15px; color: #2D1B10; background: #FAF7F2; outline: none; direction: ltr; text-align: left; transition: border-color .15s; }
.auth-input:focus { border-color: #D4875A; background: #FFFDF9; }
.auth-submit { padding: 12px 20px; background: linear-gradient(135deg, #A0704A, #D4875A); color: white; border: none; border-radius: 12px; font-family: 'Heebo', sans-serif; font-size: 15px; font-weight: 600; cursor: pointer; transition: transform .1s; }
.auth-submit:hover:not(:disabled) { transform: translateY(-1px); }
.auth-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.auth-msg { font-size: 14px; line-height: 1.6; padding: 12px 14px; border-radius: 10px; margin-top: 4px; }
.auth-msg.success { background: #E8F4EC; color: #2E7D32; border-right: 3px solid #4CAF50; }
.auth-msg.error { background: #FFF0F0; color: #C62828; border-right: 3px solid #E53935; }
.auth-fine { font-size: 12px; color: #8B7355; margin-top: 16px; line-height: 1.5; }
`;

type AuthModalProps = { onClose: () => void };

export function AuthModal({ onClose }: AuthModalProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setStatus("sending");
    setErrorMsg("");
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) {
      setStatus("error");
      setErrorMsg(error.message || "שגיאה בשליחת הקישור. נסו שוב.");
    } else {
      setStatus("sent");
      trackLead();
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="auth-overlay" onClick={onClose}>
        <div
          className="auth-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="auth-close" onClick={onClose} aria-label="סגירה">
            ×
          </button>

          <div className="auth-title">כניסה לחשבון</div>
          <div className="auth-subtitle">
            הקלידו את כתובת המייל. נשלח אליכם קישור התחברות חד‑פעמי, ללא סיסמה.
          </div>

          {status === "sent" ? (
            <>
              {/* 14.9 (Ilana, 70+): the old one-liner assumed the reader
                  knows what a "link" is and where to look. Spell out the
                  three moves, name the sender, and offer a resend right here
                  instead of sending them to hunt in folders. */}
              <div className="auth-msg success">
                <strong>נשלח אלייך מייל מ-eiforall.com.</strong>
                <br />
                1. פותחים את המייל בטלפון (הנושא: "קישור הכניסה שלך").
                <br />
                2. לוחצים על הכפתור הגדול שבתוך המייל.
                <br />
                3. הספר נפתח. בלי סיסמה, בלי תשלום נוסף.
              </div>
              <div className="auth-fine" style={{ marginTop: 12 }}>
                המייל יכול לקחת דקה. לא הגיע?{" "}
                <a
                  onClick={handleSubmit}
                  style={{ color: "#A0704A", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                >
                  שלחו לי שוב
                </a>
                {" "}· או בוואטסאפ:{" "}
                <a href="https://wa.me/972544562084" target="_blank" rel="noreferrer" style={{ color: "#1B8755", fontWeight: 600 }}>
                  054-4562084
                </a>
              </div>
            </>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit}>
              <label className="auth-label" htmlFor="auth-email">
                כתובת מייל
              </label>
              <input
                id="auth-email"
                className="auth-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                required
                disabled={status === "sending"}
              />
              <button
                type="submit"
                className="auth-submit"
                disabled={status === "sending" || !email.trim()}
              >
                {status === "sending" ? "שולח קישור…" : "שלחו לי קישור"}
              </button>
              {status === "error" && (
                <div className="auth-msg error">{errorMsg}</div>
              )}
            </form>
          )}

          <div className="auth-fine">
            בלחיצה על "שלחו לי קישור" אתם מאשרים שקראתם את תקנון השימוש ומדיניות
            הפרטיות.
          </div>
        </div>
      </div>
    </>
  );
}
